<div
  class="lingo-page"
  [attr.data-mode]="selectedMode.key"
  [class.lingo-dark]="_isDarkTheme"
  [class.lingo-light]="_isLightTheme"
  [class.lingo-paper]="_isPaperTheme"
>
  <!-- History toggle button -->
  <button
    class="lingo-history-toggle"
    mat-button
    [class.lingo-history-toggle-hidden]="historyOpen"
    (click)="toggleHistory()"
  >
    <mat-icon>{{ historyOpen ? 'chevron_left' : 'vertical_split' }}</mat-icon>
    <span class="lingo-history-toggle-label">History</span>
  </button>

  <!-- New Chat Button -->
  <button
    class="lingo-new-chat-button"
    *ngIf="this.messages.length > 0 && !historyOpen"
    mat-button
    [class.lingo-history-toggle-hidden]="historyOpen"
    (click)="startNewChat()"
  >
    <mat-icon>try</mat-icon>
    <span class="lingo-new-chat-button-label">New Chat</span>
  </button>

  <!-- History sidebar -->
  <div class="lingo-history-sidebar" [class.lingo-history-open]="historyOpen">
    <div class="lingo-history-header">
      <span class="lingo-history-title">History</span>
      <div class="lingo-history-header-actions">
        <button mat-icon-button matTooltip="New chat" (click)="startNewChat()">
          <mat-icon>try</mat-icon>
        </button>
        <button mat-icon-button matTooltip="Close" (click)="toggleHistory()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- AI Status -->
    @if (aiStatus) {
      <div class="lingo-ai-status-card">
        <div class="lingo-ai-status-row">
          <span class="lingo-ai-status-title">Subscription:</span>
          <span
            class="lingo-ai-plan-pill"
            [class.lingo-ai-plan-free]="aiStatus.subscription === 'Free Tier'"
            [class.lingo-ai-plan-plus]="aiStatus.subscription === 'Plus'"
            [class.lingo-ai-plan-pro]="aiStatus.subscription === 'Pro'"
          >
            {{ aiStatus.subscription }}
          </span>
          @if (aiStatus.subscription === 'Pro') {
            <mat-icon class="lingo-ai-plan-gem">diamond</mat-icon>
          } @else if (aiStatus.subscription === 'Plus') {
            <mat-icon class="lingo-ai-plan-gem">bolt</mat-icon>
          }
        </div>
        <div class="lingo-ai-status-bar-track">
          <div
            class="lingo-ai-status-bar-fill"
            [class.lingo-ai-status-bar-warn]="aiStatus.usage / aiStatus.limit > 0.8"
            [style.width.%]="aiStatus.limit > 0 ? (aiStatus.usage / aiStatus.limit) * 100 : 0"
          ></div>
        </div>
        <div class="lingo-ai-status-details">
          <p>{{ aiStatus.usage }} / {{ aiStatus.limit }} used ({{ aiStatus.remaining }} remaining)</p>
          <p>Ai Credits : {{ aiStatus.ai_credits }}</p>
          <p>Non-Pro users limit : 3</p>
          <p>Pro users limit : 100/day</p>
        </div>
      </div>
    }

    <div class="lingo-history-list">
      @if (historyLoading) {
        <div class="lingo-history-loading">
          <mat-spinner diameter="24"></mat-spinner>
        </div>
      } @else if (historyItems.length === 0) {
        <p class="lingo-history-empty">No conversations yet</p>
      } @else {
        @for (item of historyItems; track item.id) {
          <button
            class="lingo-history-item"
            [class.lingo-history-item-active]="activeHistoryId === item.id"
            (click)="selectSession(item)"
          >
            <mat-icon class="lingo-history-item-icon">chat_bubble_outline</mat-icon>
            <div class="lingo-history-item-text">
              <span class="lingo-history-item-title">{{ item.title }}</span>
              <span class="lingo-history-item-date">{{ item.created_at | date: 'MMM d, y' }}</span>
            </div>
          </button>
        }
      }
    </div>
  </div>

  <!-- Sidebar overlay for mobile -->
  @if (historyOpen) {
    <div class="lingo-history-overlay" (click)="toggleHistory()"></div>
  }

  <!-- Main content area -->
  <div class="lingo-main" [class.lingo-main-shifted]="historyOpen">
    @if (messages.length > 0) {
      <!-- Chat messages area (scrollable, pushes center down) -->
      <div class="lingo-chat-area">
        <div class="lingo-messages">
          @for (msg of messages; track msg.timestamp) {
            <div
              class="lingo-message"
              [class.lingo-message-ai]="msg.role === 'assistant'"
              [class.lingo-message-user]="msg.role === 'user'"
            >
              @if (msg.role === 'assistant') {
                <img class="lingo-message-avatar" alt="Lingo AI" [src]="logoSrc" />
              }
              <div class="lingo-message-bubble-wrap">
                <div class="lingo-message-bubble">
                  @if (msg.role === 'assistant') {
                    <markdown [data]="msg.displayedContent ?? msg.parsedContent?.text ?? msg.content"></markdown>
                    @if (msg.typing) {
                      <span class="lingo-typewriter-cursor"></span>
                    }
                  } @else {
                    <p>{{ msg.content }}</p>
                  }
                </div>

                <!-- User message: attached images -->
                @if (msg.role === 'user' && msg.attachments?.attached_images?.length) {
                  <div class="lingo-user-attachments">
                    @for (img of msg.attachments!.attached_images!; track img) {
                      <img class="lingo-attached-image" alt="Attached image" [src]="assets + img" />
                    }
                  </div>
                }

                <!-- User message: attached DDC content -->
                @if (msg.role === 'user' && msg.attachments?.attached_ddc_contents?.length) {
                  <div class="lingo-user-attachments">
                    @for (ac of msg.attachments!.attached_ddc_contents!; track ac.object_id) {
                      <div class="lingo-attached-content-chip">
                        <mat-icon>{{ getContentTypeInfo(ac.content_type_id).icon }}</mat-icon>
                        <span>1 {{ getContentTypeInfo(ac.content_type_id).label }} attached</span>
                      </div>
                    }
                  </div>
                }

                <!-- AI message: dictionary cards -->
                @if (msg.role === 'assistant' && msg.parsedContent?.dictionaries?.length) {
                  <div class="lingo-dictionaries-list">
                    @for (dic of msg.parsedContent!.dictionaries!; track dic.id) {
                      <button
                        class="lingo-dictionary-card"
                        (click)="router.navigate(['/library/dic/' + dic.id + '/' + dic.owner_username])"
                      >
                        <mat-icon class="lingo-dictionary-card-icon">auto_stories</mat-icon>
                        <span class="lingo-dictionary-card-title">{{ dic.title || dic.name || 'Dictionary' }}</span>
                        <mat-icon class="lingo-dictionary-card-arrow">chevron_right</mat-icon>
                      </button>
                    }
                  </div>
                }

                <!-- AI message: created content card -->
                @if (msg.role === 'assistant' && msg.created_content_type && msg.created_object_id) {
                  <button class="lingo-created-content-card" (click)="navigateToCreatedContent(msg)">
                    <div class="lingo-created-content-icon">
                      <mat-icon>{{ getCreatedContentInfo(msg.created_content_type).icon }}</mat-icon>
                    </div>
                    <div class="lingo-created-content-info">
                      <span class="lingo-created-content-label"
                        >{{ getCreatedContentInfo(msg.created_content_type).label }} Created</span
                      >
                      <span class="lingo-created-content-title">{{ msg.created_content || 'View content' }}</span>
                    </div>
                    <mat-icon class="lingo-created-content-arrow">arrow_forward</mat-icon>
                  </button>
                }

                @if (msg.role === 'assistant') {
                  <div class="lingo-message-actions">
                    <button
                      class="lingo-action-btn"
                      mat-icon-button
                      matTooltip="{{ msg.copied ? 'Copied!' : 'Copy' }}"
                      [class.lingo-action-active]="msg.copied"
                      (click)="copyMessage(msg)"
                    >
                      <mat-icon>{{ msg.copied ? 'check' : 'content_copy' }}</mat-icon>
                    </button>
                    <button
                      class="lingo-action-btn"
                      mat-icon-button
                      matTooltip="Like"
                      [class.lingo-action-active]="msg.feedback === 'liked'"
                      (click)="likeMessage(msg)"
                    >
                      <mat-icon>{{ msg.feedback === 'liked' ? 'thumb_up' : 'thumb_up_off_alt' }}</mat-icon>
                    </button>
                    <button
                      class="lingo-action-btn"
                      mat-icon-button
                      matTooltip="Dislike"
                      [class.lingo-action-active]="msg.feedback === 'disliked'"
                      (click)="dislikeMessage(msg)"
                    >
                      <mat-icon>{{ msg.feedback === 'disliked' ? 'thumb_down' : 'thumb_down_off_alt' }}</mat-icon>
                    </button>
                  </div>
                }
              </div>
            </div>
          }
          @if (isLoadingSession) {
            <div class="lingo-session-loading">
              <mat-spinner diameter="28"></mat-spinner>
              <span class="lingo-session-loading-text">Loading messages...</span>
            </div>
          }
          @if (isLoading && !isLoadingSession) {
            <div class="lingo-message lingo-message-ai">
              <img class="lingo-message-avatar" alt="Lingo AI" [src]="logoSrc" />
              <div class="lingo-message-bubble lingo-typing">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
              </div>
            </div>
          }
          @if (isGenerating) {
            <div class="lingo-message lingo-message-ai">
              <img class="lingo-message-avatar" alt="Lingo AI" [src]="logoSrc" />
              <div class="lingo-generating-card">
                <div class="lingo-generating-text">
                  <span>Generating</span>
                  <div class="lingo-generating-dots">
                    <span class="wave-dot"></span>
                    <span class="wave-dot"></span>
                    <span class="wave-dot"></span>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    }

    <!-- Centered input area -->
    <div class="lingo-center" [class.lingo-center-has-messages]="messages.length > 0">
      @if (messages.length === 0) {
        <div class="lingo-logo-wrapper">
          <img class="lingo-logo" alt="Lingo AI" [src]="logoSrc" />
        </div>
        <p class="lingo-help-text">{{ selectedMode.helpText }}</p>
      }

      <!-- Mode buttons row -->
      @if (messages.length === 0) {
        <div class="lingo-modes-row">
          @for (mode of modes; track mode.key) {
            <button
              class="lingo-mode-btn"
              [class.lingo-mode-active]="selectedMode.key === mode.key"
              (click)="selectMode(mode)"
            >
              <mat-icon>{{ mode.icon }}</mat-icon>
              <span class="lingo-mode-label">{{ mode.label }}</span>
            </button>
          }
        </div>
      }

      <!-- Input -->
      <div class="lingo-input-wrapper">
        <!-- Hidden file input for images -->
        <input #imageInput accept="image/*" style="display: none" type="file" (change)="onImageSelected($event)" />

        <!-- Upper: textarea -->
        <textarea
          #textareaRef
          class="lingo-textarea"
          placeholder="Ask to create something..."
          rows="1"
          [maxlength]="MAX_CHARS"
          [(ngModel)]="messageText"
          (input)="autoResize(textareaRef)"
          (keydown)="onKeyDown($event)"
        ></textarea>

        <!-- Footer: add button, model selector, char count, send button -->
        <div class="lingo-input-footer">
          <button
            class="lingo-attach-btn"
            mat-icon-button
            matTooltip="Add images and more"
            [matMenuTriggerFor]="attachMenu"
          >
            <mat-icon>add</mat-icon>
          </button>

          <mat-menu #attachMenu="matMenu">
            <button mat-menu-item (click)="triggerImagePicker()">
              <mat-icon>attach_file</mat-icon>
              <span>Upload image</span>
            </button>
            <button mat-menu-item (click)="openContentPicker('dictionary')">
              <mat-icon>auto_stories</mat-icon>
              <span>Attach dictionary</span>
            </button>
            <button mat-menu-item (click)="openContentPicker('note')">
              <mat-icon>text_snippet</mat-icon>
              <span>Attach note</span>
            </button>
          </mat-menu>

          <div class="lingo-input-actions">
            <!-- Model tier selector -->
            <button class="lingo-model-btn" mat-button matTooltip="Choose AI model" [matMenuTriggerFor]="modelMenu">
              <span class="lingo-model-btn-label">{{ selectedModelTier | titlecase }}</span>
              <mat-icon style="color: gray">expand_more</mat-icon>
            </button>

            <mat-menu #modelMenu="matMenu">
              @for (option of modelOptions; track option.tier) {
                <button
                  class="lingo-model-item"
                  mat-menu-item
                  [class.lingo-model-item-active]="selectedModelTier === option.tier"
                  [class.lingo-model-item-locked]="!isTierAllowed(option.tier)"
                  [disabled]="!isTierAllowed(option.tier)"
                  (click)="selectModelTier(option)"
                >
                  <mat-icon class="lingo-model-item-icon">
                    {{ selectedModelTier === option.tier ? 'check' : getTierIcon(option.tier) }}
                  </mat-icon>
                  <span class="lingo-model-item-label">{{ option.label }}</span>
                  @if (!isTierAllowed(option.tier)) {
                    <span class="lingo-model-upgrade" (click)="$event.stopPropagation(); router.navigate(['/update'])"
                      >Upgrade</span
                    >
                  }
                </button>
              }
            </mat-menu>

            <span
              class="lingo-char-count"
              [class.lingo-char-limit]="charCount >= MAX_CHARS"
              [class.lingo-char-warn]="charCount > MAX_CHARS * 0.9"
            >
              {{ charCount }}/{{ MAX_CHARS }}
            </span>
            <button
              class="lingo-send-btn"
              mat-icon-button
              matTooltip="Send"
              [disabled]="!canSend"
              (click)="sendMessage()"
            >
              <mat-icon>send</mat-icon>
            </button>
          </div>
        </div>
      </div>
      <!-- Attachment previews row (above input) -->
      @if (selectedContent || selectedImagePreview) {
        <div class="lingo-attachments-row">
          @if (selectedContent) {
            <div class="lingo-selected-content-chip">
              <mat-icon>{{ selectedContent.type === 'dictionary' ? 'auto_stories' : 'text_snippet' }}</mat-icon>
              <span class="lingo-selected-content-title">{{ selectedContent.title }}</span>
              <button class="lingo-selected-content-remove" (click)="removeSelectedContent()">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          }
          @if (selectedImagePreview) {
            <div class="lingo-selected-image-preview">
              <img alt="Selected image" [src]="selectedImagePreview" />
              <button class="lingo-selected-image-remove" (click)="removeSelectedImage()">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          }
        </div>
      }
      <!-- Suggestion cards -->
      @if (messages.length === 0 && suggestions.length > 0) {
        <div class="lingo-suggestions">
          @for (suggestion of suggestions; track suggestion) {
            <button class="lingo-suggestion-card" (click)="selectSuggestion(suggestion)">
              {{ suggestion }}
            </button>
          }
        </div>
      }
    </div>
  </div>

  <!-- Content Picker Overlay (Dictionaries / Notes) -->
  @if (pickerOpen) {
    <div class="lingo-picker-overlay" (click)="closeContentPicker()">
      <div class="lingo-picker-dialog" (click)="$event.stopPropagation()">
        <div class="lingo-picker-header">
          <span class="lingo-picker-title">
            {{ pickerType === 'dictionary' ? 'Select Dictionary' : 'Select Note' }}
          </span>
          <button class="close-dialog-btn" mat-icon-button matTooltip="Close" (click)="closeContentPicker()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <div class="lingo-picker-search">
          <mat-icon>search</mat-icon>
          <input placeholder="Search..." type="text" [(ngModel)]="pickerSearch" />
        </div>
        <div class="lingo-picker-list">
          @if (pickerLoading) {
            <div class="lingo-picker-loading">
              <mat-spinner diameter="24"></mat-spinner>
            </div>
          } @else if (filteredPickerItems.length === 0) {
            <p class="lingo-picker-empty">
              {{ pickerSearch ? 'No results found' : 'No ' + pickerType + 's found' }}
            </p>
          } @else {
            @for (item of filteredPickerItems; track item.id) {
              <button class="lingo-picker-item" (click)="selectPickerItem(item)">
                <mat-icon>{{ pickerType === 'dictionary' ? 'auto_stories' : 'text_snippet' }}</mat-icon>
                <span class="lingo-picker-item-title">{{ item.title || item.label || 'Untitled' }}</span>
              </button>
            }
          }
        </div>
      </div>
    </div>
  }
</div>
